'use client'

import { Card, CardContent, styled, Box, Fade, Skeleton } from '@mui/material'
import { ReactNode, memo, useCallback, useMemo, useState } from 'react'
import { Text } from '@components/Helpers'
import CardInfoBox from '@components/CardInfoBox/CardInfoBox'
import { useTranslation } from 'react-i18next'
import Link from 'next/link'
import CatImage from './CatImage'
import RemoteImage from './RemoteImage'
import { CatHoverContext } from './CatHoverLink'

const Root = styled(Card)({
  // One flat surface, one radius, nothing drawn around it. A legendary used to
  // be marked by a gold outline; it is a tag on the artwork now (see below), so
  // every card in the grid reads the same.
  background: 'var(--dewi_surface_card) !important',
  backgroundColor: 'var(--dewi_surface_card) !important',
  border: 'none !important',
  borderWidth: 0,
  boxShadow: 'none',
  borderRadius: 'var(--dewi_radius_lg)',
  transition: 'transform .2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.03)',
  },
})

const SkeletonCard = styled(Skeleton)({
  backgroundColor: 'var(--dewi_colors_gray_9)',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '100%',
  borderRadius: 2,
})

const CardContainer = styled(Box)({
  position: 'relative',
  display: 'contents',
})

const CardImage = styled(Box)({
  width: '100%',
  borderRadius: 'var(--dewi_radius_lg) var(--dewi_radius_lg) 0 0',
  overflow: 'hidden',
  lineHeight: 0,
})

const TextContainer = styled(Box)({
  // The name sits directly on the card's surface: no plate, no outline, just
  // the artwork above it and space around it.
  border: 'none',
  backgroundColor: 'transparent',
  borderRadius: 0,
  padding: 10,
})

const StyledText = styled('span')({
  textAlign: 'center',
  // Never inherit MUI's default (near black) body text: the plate behind this
  // is always dark, so the name is always light.
  color: 'var(--dewi_colors_gray_0)',
  fontWeight: 'semibold',
  display: '-webkit-box',
  overflow: 'hidden',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 1,
  fontSize: 14,
})

const ContentContainer = styled(CardContent)({
  // The card used to be filled with the cat's own dominant colour. It is the
  // same dark surface for every cat now; the artwork is the only colour on it.
  backgroundColor: 'transparent',
  padding: '0 10px 10px',
  borderRadius: '0 0 var(--dewi_radius_lg) var(--dewi_radius_lg)',
  paddingBottom: '10px !important',
})

const CornerTag = styled(Box)({
  position: 'absolute',
  top: 4,
  right: 4,
  borderRadius: 'var(--dewi_radius_rounded)',
  padding: '2px 8px',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  backgroundColor: 'rgba(0, 0, 0, 0.72)',
})

/** Burnt is a real state, so it is the one tag that keeps a colour. */
const BurntBadge = styled(CornerTag)({
  color: 'var(--dewi_colors_red_4)',
})

/**
 * The legendary marker.
 *
 * It used to be a gold outline around the whole card. It is a small white tag
 * on the artwork now: the same shape as every other pill in the app, and the
 * only thing that distinguishes a legendary tile from a normal one.
 */
const LegendaryBadge = styled(CornerTag)({
  top: 4,
  right: 'auto',
  left: 4,
  color: 'var(--dewi_colors_dark_9)',
  backgroundColor: 'var(--dewi_colors_gray_0)',
})

interface DewiCardProps {
  name: string
  /**
   * Cat number. The card then renders the self hosted artwork from
   * /cats/<n>.png (swapping to the GIF on hover) and opens the cat in the
   * explorer.
   */
  n?: number
  /**
   * Explicit image source, for cards that are not part of the cat collection
   * (Helium hotspots in the holdings view).
   */
  imageURL?: string
  /** False when there is no artwork for this cat yet. */
  hasImage?: boolean
  showListed?: boolean
  isLegendary?: boolean
  isBurnt?: boolean
  isStaked?: boolean
  /** Overrides the default explorer destination. */
  href?: string
  /** Rendered over the top-left corner of the artwork. */
  badge?: ReactNode
}

/**
 * A cat (or hotspot) tile.
 *
 * Everything it shows is known up front: the artwork is self hosted and the
 * fill colour is precomputed by the manifest, so there is no image proxy, no
 * runtime colour extraction and no chain access. Only the hover-to-animate
 * behaviour needs the browser.
 */
function DewiCard({
  name,
  n,
  imageURL,
  hasImage = true,
  showListed,
  isLegendary = false,
  isBurnt = false,
  isStaked = false,
  href,
  badge,
}: DewiCardProps) {
  // A cat's artwork is self hosted and its fill colour is precomputed, so there
  // is nothing to wait for: the card is visible in the server-rendered HTML.
  // Hotspot art comes from a remote URL that may never resolve, so those cards
  // keep the skeleton-then-fade the whole grid used to have.
  const isLocalArtwork = typeof n === 'number'
  const [isImageLoaded, setIsImageLoaded] = useState(isLocalArtwork)
  const [hovered, setHovered] = useState(false)
  const { t } = useTranslation()

  const onImageLoaded = useCallback(() => setIsImageLoaded(true), [])
  const animate = useCallback(() => setHovered(true), [])
  const stopAnimating = useCallback(() => setHovered(false), [])

  const destination =
    href ||
    (typeof n === 'number'
      ? `/explorer/overview?dewi=${encodeURIComponent(n)}`
      : undefined)

  const cardContent = useMemo(
    () => (
      <>
        <Fade appear={false} in={isImageLoaded} timeout={1000}>
          <CardImage>
            {typeof n === 'number' ? (
              <CatImage
                n={n}
                alt={name}
                hasImage={hasImage}
                onLoad={onImageLoaded}
              />
            ) : (
              <RemoteImage src={imageURL} alt={name} onLoad={onImageLoaded} />
            )}
          </CardImage>
        </Fade>

        {badge && (
          <Fade appear={false} in={isImageLoaded} timeout={1000}>
            <Box display="flex" position="absolute">
              {badge}
            </Box>
          </Fade>
        )}

        {showListed && (
          <Fade appear={false} in={isImageLoaded} timeout={1000}>
            <Box display="flex" position="absolute">
              <CardInfoBox color="listed">
                <Text sx={{ fontSize: 15 }}>
                  {t('explorerPage.overview.cardInfo.listed')}
                </Text>
              </CardInfoBox>
            </Box>
          </Fade>
        )}

        {isLegendary && <LegendaryBadge>Legendary</LegendaryBadge>}

        {isBurnt && <BurntBadge>Burnt</BurntBadge>}

        <Fade appear={false} in={isImageLoaded} timeout={1000}>
          <ContentContainer>
            <TextContainer
              className={
                isStaked
                  ? 'bg-[url("/assets/frozenOverlay.png")] bg-no-repeat bg-center bg-cover'
                  : ''
              }
            >
              <StyledText>{name}</StyledText>
            </TextContainer>
          </ContentContainer>
        </Fade>
      </>
    ),
    [
      isImageLoaded,
      n,
      name,
      hasImage,
      imageURL,
      onImageLoaded,
      badge,
      showListed,
      isBurnt,
      isLegendary,
      isStaked,
      t,
    ],
  )

  const Wrapper = useMemo(
    () =>
      destination
        ? ({ children }: { children: ReactNode }) => (
            <Link
              href={destination}
              style={{ position: 'relative', display: 'contents' }}
            >
              {children}
            </Link>
          )
        : CardContainer,
    [destination],
  )

  return (
    // `appear={false}`: with the default the server-rendered card is opacity 0
    // until hydration runs the entrance animation, so the whole grid is blank
    // in the HTML. The transition still runs when a remote image resolves.
    <Fade appear={false} in timeout={1000}>
      <Box
        onMouseEnter={animate}
        onMouseLeave={stopAnimating}
        onFocus={animate}
        onBlur={stopAnimating}
      >
        <Root>
          {!isImageLoaded && <SkeletonCard variant="rectangular" />}
          <CatHoverContext.Provider value={hovered}>
            <Wrapper>{cardContent}</Wrapper>
          </CatHoverContext.Provider>
        </Root>
      </Box>
    </Fade>
  )
}

export default memo(DewiCard)
